import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Search, TrendingUp, AlertTriangle, CheckCircle2, Clock, Download } from 'lucide-react';
import axios from 'axios';
import { useSectorTerms } from '@/utils/useSectorTerms';

export const AdminReports = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const labels = user?.labels || { people: 'Kullanıcılar', payment: 'Ödeme', identifier: 'Numara' };
  const terms = useSectorTerms();

  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [unitData, setUnitData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('overview');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [summaryRes, monthlyRes, unitRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/monthly`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/by-unit`, { withCredentials: true }),
      ]);
      setSummary(summaryRes.data);
      setMonthlyData(monthlyRes.data);
      setUnitData(unitRes.data);
    } catch (error) {
      console.error('Raporlar yüklenemedi:', error);
    }
  };

  const filteredUnits = unitData.filter(u => {
    const term = searchTerm.toLowerCase();
    return u.name.toLowerCase().includes(term)
      || u.email.toLowerCase().includes(term)
      || (u.apartment_number || '').toLowerCase().includes(term);
  });

  const downloadReport = async (type) => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/download/${type}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const filenames = { summary: 'rapor_ozet.xlsx', 'by-unit': 'birim_rapor.xlsx', monthly: 'aylik_rapor.xlsx' };
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filenames[type] || 'rapor.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('İndirme hatası:', error);
    }
  };

  return (
    <PageLayout>
      
      
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="reports-page-title">
                Raporlar
              </h1>
              <p className="text-sm sm:text-base text-[#595959] mt-2">Gelir, ödeme ve {terms.customerNoLabel.toLocaleLowerCase('tr-TR')} bazlı raporlar</p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant={activeView === 'overview' ? 'default' : 'outline'}
                onClick={() => setActiveView('overview')}
                className={`flex-1 sm:flex-none text-sm ${activeView === 'overview' ? 'bg-[#2563EB] text-white' : ''}`}
                data-testid="view-overview-btn"
              >
                Genel Bakış
              </Button>
              <Button
                variant={activeView === 'units' ? 'default' : 'outline'}
                onClick={() => setActiveView('units')}
                className={`flex-1 sm:flex-none text-sm ${activeView === 'units' ? 'bg-[#2563EB] text-white' : ''}`}
                data-testid="view-units-btn"
              >
                {terms.customerNoLabel} Bazlı
              </Button>
            </div>
          </div>

          {/* Overview */}
          {activeView === 'overview' && summary && (
            <div className="space-y-6">
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadReport('summary')} className="gap-1.5" data-testid="download-summary-btn">
                  <Download className="w-4 h-4" /> Özet İndir
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadReport('monthly')} className="gap-1.5" data-testid="download-monthly-btn">
                  <Download className="w-4 h-4" /> Aylık İndir
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border border-[#E5E5E5]" data-testid="report-paid-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg"><CheckCircle2 className="w-5 h-5 text-green-700" /></div>
                      <span className="text-sm text-[#595959]">Toplam Tahsilat</span>
                    </div>
                    <p className="text-3xl font-bold text-[#16A34A]">{summary.total_paid.toFixed(2)} TL</p>
                    <p className="text-sm text-[#595959] mt-1">{summary.paid_count} ödeme</p>
                  </CardContent>
                </Card>

                <Card className="border border-[#E5E5E5]" data-testid="report-pending-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-amber-100 rounded-lg"><Clock className="w-5 h-5 text-amber-700" /></div>
                      <span className="text-sm text-[#595959]">Bekleyen</span>
                    </div>
                    <p className="text-3xl font-bold text-[#1A1A1A]">{summary.total_pending.toFixed(2)} TL</p>
                    <p className="text-sm text-[#595959] mt-1">{summary.pending_count} ödeme</p>
                  </CardContent>
                </Card>

                <Card className="border border-[#E5E5E5]" data-testid="report-overdue-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-700" /></div>
                      <span className="text-sm text-[#595959]">Gecikmiş</span>
                    </div>
                    <p className="text-3xl font-bold text-[#DC2626]">{summary.total_overdue.toFixed(2)} TL</p>
                    <p className="text-sm text-[#595959] mt-1">{summary.overdue_count} ödeme</p>
                  </CardContent>
                </Card>
              </div>

              {monthlyData.length > 0 && (
                <Card className="border border-[#E5E5E5]" data-testid="monthly-chart-card">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#2563EB]" />
                      Aylık Tahsilat Grafiği
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis dataKey="month" stroke="#595959" />
                        <YAxis stroke="#595959" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5', borderRadius: '8px' }}
                          formatter={(value) => [`${value.toFixed(2)} TL`, 'Tahsilat']}
                        />
                        <Bar dataKey="amount" fill="#2563EB" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Unit-Based Report */}
          {activeView === 'units' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#595959]" />
                  <Input
                    placeholder={`${terms.customerNoLabel}, isim veya e-posta ile ara...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="unit-search-input"
                  />
                </div>
                <div className="flex items-center gap-2 justify-between sm:justify-start">
                  <span className="text-sm text-[#595959]">{filteredUnits.length} kayıt</span>
                  <Button variant="outline" size="sm" onClick={() => downloadReport('by-unit')} className="gap-1.5" data-testid="download-unit-btn">
                    <Download className="w-4 h-4" /> <span className="hidden sm:inline">Excel</span> İndir
                  </Button>
                </div>
              </div>

              {/* Summary cards for unit view */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border border-green-200 bg-green-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-green-700">Sorunsuz</p>
                    <p className="text-2xl font-bold text-green-800" data-testid="units-clean-count">
                      {unitData.filter(u => u.total_debt === 0).length}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-amber-200 bg-amber-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-amber-700">Bekleyen Borcu Var</p>
                    <p className="text-2xl font-bold text-amber-800" data-testid="units-pending-count">
                      {unitData.filter(u => u.total_pending > 0 && u.total_overdue === 0).length}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-red-200 bg-red-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-red-700">Gecikmiş Borcu Var</p>
                    <p className="text-2xl font-bold text-red-800" data-testid="units-overdue-count">
                      {unitData.filter(u => u.total_overdue > 0).length}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Desktop table */}
              <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden hidden md:block" data-testid="unit-report-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9F8F6]">
                      <TableHead>{terms.customerNoLabel}</TableHead>
                      <TableHead>İsim</TableHead>
                      <TableHead>E-posta</TableHead>
                      <TableHead className="text-right">Ödenen</TableHead>
                      <TableHead className="text-right">Bekleyen</TableHead>
                      <TableHead className="text-right">Gecikmiş</TableHead>
                      <TableHead className="text-right">Toplam Borç</TableHead>
                      <TableHead>Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUnits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-[#595959]">
                          {searchTerm ? 'Arama sonucu bulunamadı' : 'Kayıt bulunamadı'}
                        </TableCell>
                      </TableRow>
                    ) : filteredUnits.map((u) => {
                      const status = u.total_overdue > 0 ? 'overdue' : u.total_pending > 0 ? 'pending' : 'clean';
                      const statusStyles = { clean: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', overdue: 'bg-red-100 text-red-700' };
                      const statusLabelsMap = { clean: 'Temiz', pending: 'Bekliyor', overdue: 'Gecikmiş' };
                      return (
                        <TableRow key={u.user_id} className={status === 'overdue' ? 'bg-red-50/30' : ''} data-testid={`unit-row-${u.user_id}`}>
                          <TableCell className="font-medium">{u.apartment_number || '-'}</TableCell>
                          <TableCell>{u.name}</TableCell>
                          <TableCell className="text-[#595959] text-sm">{u.email}</TableCell>
                          <TableCell className="text-right font-medium text-green-700">{u.total_paid.toFixed(2)} TL</TableCell>
                          <TableCell className="text-right font-medium text-amber-700">{u.total_pending > 0 ? `${u.total_pending.toFixed(2)} TL` : '-'}</TableCell>
                          <TableCell className="text-right font-medium text-red-700">{u.total_overdue > 0 ? `${u.total_overdue.toFixed(2)} TL` : '-'}</TableCell>
                          <TableCell className="text-right font-bold">{u.total_debt > 0 ? `${u.total_debt.toFixed(2)} TL` : '-'}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status]}`} data-testid={`unit-status-${u.user_id}`}>
                              {statusLabelsMap[status]}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3" data-testid="unit-mobile-cards">
                {filteredUnits.length === 0 ? (
                  <p className="text-center py-8 text-[#595959]">{searchTerm ? 'Arama sonucu bulunamadı' : 'Kayıt bulunamadı'}</p>
                ) : filteredUnits.map((u) => {
                  const status = u.total_overdue > 0 ? 'overdue' : u.total_pending > 0 ? 'pending' : 'clean';
                  const statusStyles = { clean: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', overdue: 'bg-red-100 text-red-700' };
                  const statusLabelsMap = { clean: 'Temiz', pending: 'Bekliyor', overdue: 'Gecikmiş' };
                  return (
                    <div key={u.user_id} className={`bg-white border rounded-xl p-4 ${status === 'overdue' ? 'border-red-200' : 'border-[#E5E5E5]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">{u.name}</p>
                          <p className="text-xs text-[#595959]">{u.apartment_number || '-'} &middot; {u.email}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status]}`}>{statusLabelsMap[status]}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div className="p-2 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600">Ödenen</p>
                          <p className="font-bold text-green-700">{u.total_paid.toFixed(0)} TL</p>
                        </div>
                        <div className="p-2 bg-amber-50 rounded-lg">
                          <p className="text-xs text-amber-600">Bekleyen</p>
                          <p className="font-bold text-amber-700">{u.total_pending.toFixed(0)} TL</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded-lg">
                          <p className="text-xs text-red-600">Gecikmiş</p>
                          <p className="font-bold text-red-700">{u.total_overdue.toFixed(0)} TL</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      
    </PageLayout>
  );
};
